import React, { useMemo } from "react";
import { Button, Col, Form, Row, Select, Typography } from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { useTranslate, useCustom, useApiUrl } from "@refinedev/core";
import { CalculatorOptions } from "../../../shared/constants/calculatorOptions";
import {
  ProductRuleField,
  ProductRuleOperator,
  type IProductRuleFieldMetadata,
  type IProductRuleMetadata,
} from "../../../models/product-rule";

interface ConditionsBuilderProps {
  /** Rule builder metadata; the component renders nothing until it arrives. */
  metadata?: IProductRuleMetadata;
  /** Current conditions, watched from the parent form to drive dependent UI. */
  conditions: Array<{ field?: ProductRuleField; values?: string[] } | undefined>;
  onChange: () => void;
  disabled?: boolean;
}

/**
 * Builds a rule's conditions from the server-provided metadata, so no field
 * name, operator or value list is hardcoded here.
 *
 * Two behaviours exist to keep the UI from building a rule the API rejects:
 * a field already used by another row is disabled (one condition per field),
 * and the operator list is narrowed to the ones the chosen field accepts.
 */
export const ConditionsBuilder: React.FC<ConditionsBuilderProps> = ({
  metadata,
  conditions,
  onChange,
  disabled,
}) => {
  const _t = useTranslate();
  const apiUrl = useApiUrl();

  const fieldByKey = useMemo(() => {
    const map = new Map<ProductRuleField, IProductRuleFieldMetadata>();
    (metadata?.fields ?? []).forEach((field) => map.set(field.key, field));
    return map;
  }, [metadata]);

  const operatorLabel = useMemo(() => {
    const map = new Map<ProductRuleOperator, string>();
    (metadata?.operators ?? []).forEach((op) => map.set(op.key, op.label));
    return map;
  }, [metadata]);

  /** Makes chosen in the `make` row, used to narrow the model suggestions. */
  const selectedMakes = useMemo(
    () =>
      conditions.find((condition) => condition?.field === ProductRuleField.make)
        ?.values ?? [],
    [conditions],
  );

  const { data: modelsData } = useCustom<string[]>({
    url: `${apiUrl}/car-ref/models`,
    method: "get",
    config: { query: { make: selectedMakes[0] } },
    queryOptions: {
      enabled: selectedMakes.length === 1,
      queryKey: ["car-ref-models", selectedMakes[0]],
    },
  });

  const usedFields = useMemo(
    () => new Set(conditions.map((condition) => condition?.field).filter(Boolean)),
    [conditions],
  );

  const renderValueControl = (
    field: ProductRuleField | undefined,
    placeholder: string,
  ) => {
    const meta = field ? fieldByKey.get(field) : undefined;

    // Reference fields (account group) are a closed list: a typed value could
    // never match anything the backend knows about.
    if (meta?.valueType === "reference") {
      return (
        <Select
          mode="multiple"
          options={meta.options ?? []}
          placeholder={placeholder}
          className="w-full"
          maxTagCount="responsive"
          showSearch
          optionFilterProp="label"
          disabled={disabled}
        />
      );
    }

    // ECU reuses the very list the customer's order form offers, so an admin
    // cannot type a brand the dropdown will never produce.
    if (field === ProductRuleField.ecu) {
      return (
        <Select
          mode="multiple"
          options={CalculatorOptions.map((option) => ({
            label: option,
            value: option,
          }))}
          placeholder={placeholder}
          className="w-full"
          maxTagCount="responsive"
          showSearch
          optionFilterProp="label"
          disabled={disabled}
        />
      );
    }

    // The remaining vehicle fields are free text on the order form, so these
    // are suggestions rather than a closed list — `tags` keeps them typeable.
    const suggestions =
      field === ProductRuleField.model && modelsData?.data?.length
        ? modelsData.data
        : (meta?.suggestions ?? []);

    return (
      <Select
        mode="tags"
        options={suggestions.map((value) => ({ label: value, value }))}
        placeholder={placeholder}
        className="w-full"
        maxTagCount="responsive"
        tokenSeparators={[","]}
        disabled={disabled}
      />
    );
  };

  return (
    <Form.List name="conditions">
      {(fields, { add, remove }) => (
        <>
          {fields.length === 0 && (
            <Typography.Text type="secondary" className="mb-3 block text-sm">
              {_t(
                "product-rules.fields.noConditionsHint",
                "Add at least one condition — a rule with none never blocks anything.",
              )}
            </Typography.Text>
          )}

          {fields.map(({ key, name, ...restField }) => {
            const current = conditions[name];
            const meta = current?.field ? fieldByKey.get(current.field) : undefined;
            const availableOperators = meta?.operators ?? [];

            return (
              <Row key={key} gutter={8} align="top" className="mb-2">
                <Col xs={24} sm={7}>
                  <Form.Item
                    {...restField}
                    name={[name, "field"]}
                    rules={[
                      {
                        required: true,
                        message: _t("fields.errors.required", "Required"),
                      },
                    ]}
                    className="!mb-2"
                  >
                    <Select
                      placeholder={_t("product-rules.fields.field", "Field")}
                      disabled={disabled}
                      options={(metadata?.fields ?? []).map((field) => ({
                        label: field.label,
                        value: field.key,
                        // One condition per field: the API rejects duplicates.
                        disabled:
                          field.key !== current?.field && usedFields.has(field.key),
                      }))}
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={6}>
                  <Form.Item
                    {...restField}
                    name={[name, "operator"]}
                    rules={[
                      {
                        required: true,
                        message: _t("fields.errors.required", "Required"),
                      },
                    ]}
                    className="!mb-2"
                  >
                    <Select
                      placeholder={_t("product-rules.fields.operator", "Operator")}
                      disabled={disabled || !current?.field}
                      options={availableOperators.map((operator) => ({
                        label: operatorLabel.get(operator) ?? operator,
                        value: operator,
                      }))}
                    />
                  </Form.Item>
                </Col>

                <Col xs={20} sm={9}>
                  <Form.Item
                    {...restField}
                    name={[name, "values"]}
                    rules={[
                      {
                        required: true,
                        message: _t("fields.errors.required", "Required"),
                      },
                    ]}
                    className="!mb-2"
                  >
                    {renderValueControl(
                      current?.field,
                      _t("product-rules.fields.values", "Values"),
                    )}
                  </Form.Item>
                </Col>

                <Col xs={4} sm={2}>
                  <Button
                    danger
                    icon={<DeleteOutlined />}
                    disabled={disabled}
                    aria-label={_t("buttons.delete", "Delete")}
                    onClick={() => {
                      remove(name);
                      onChange();
                    }}
                  />
                </Col>
              </Row>
            );
          })}

          <Button
            type="dashed"
            icon={<PlusOutlined />}
            disabled={disabled}
            onClick={() => {
              add({ field: undefined, operator: undefined, values: [] });
              onChange();
            }}
            className="w-full"
          >
            {_t("product-rules.actions.addCondition", "Add condition")}
          </Button>
        </>
      )}
    </Form.List>
  );
};
